import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../utils/AppContext';
import { isLocalEnv } from '../utils/env';

const Component = ({ showCaptchaError }: { showCaptchaError: boolean }) => {
  const { t } = useTranslation();
  return (
    <>
      <div id="google-recaptcha-container" className="recaptcha"></div>
      {showCaptchaError && (
        <div data-testid="recaptcha-error" className="recaptcha-error-container">
          <span className="recaptcha-error">{t('captchaError')}</span>
        </div>
      )}
    </>
  );
};

const useCaptcha = () => {
  const [isCaptchaError, setIsCaptchaError] = useState(false);
  const [isCaptchaRendered, setIsCaptchaRendered] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>();
  const { activeConfigurationsURL } = useContext(AppContext);

  const isLocalEnvValue = useMemo(() => isLocalEnv(), []);

  useEffect(() => {
    const onScriptLoaded = () => {
      setIsScriptLoaded(true);
    };
    document.getElementById('grecaptcha-script')?.addEventListener('load', onScriptLoaded);
    return () =>
      document.getElementById('grecaptcha-script')?.removeEventListener('load', onScriptLoaded);
  }, []);

  const CaptchaComponent = useMemo(() => Component, []);

  useEffect(() => {
    if (activeConfigurationsURL) {
      // Get captcha site key from server environment variables
      axios
        .get(`${activeConfigurationsURL.origin}/key/recaptcha`)
        .then((res) => setCaptchaSiteKey(res?.data?.siteKey))
        .catch(() => {
          // catch failed request and ignore it
        });
    }
  }, [activeConfigurationsURL]);

  const renderCaptcha = useCallback(() => {
    if (
      !isLocalEnvValue &&
      (isScriptLoaded || window.grecaptcha) &&
      !isCaptchaRendered &&
      captchaSiteKey
    ) {
      window.grecaptcha?.ready(() => {
        const params = {
          sitekey: captchaSiteKey,
          theme: 'dark',
          callback: () => setIsCaptchaError(false),
        };
        window.grecaptcha?.render('google-recaptcha-container', params);
        setIsCaptchaRendered(true);
      });
    }
  }, [isLocalEnvValue, isScriptLoaded, isCaptchaRendered, captchaSiteKey]);

  const resetCaptcha = () => {
    if (!isLocalEnvValue && isCaptchaRendered) {
      window.grecaptcha?.reset();
    }
  };

  const getCaptchaResponse = () => ({
    shouldCheck: isCaptchaRendered && !isLocalEnvValue,
    response:
      isCaptchaRendered && !isLocalEnvValue
        ? (window.grecaptcha?.getResponse() as string)
        : undefined,
  });

  const showCaptchaError = useCallback(() => {
    isCaptchaRendered && setIsCaptchaError(true);
  }, [isCaptchaRendered]);

  return {
    CaptchaComponent,
    renderCaptcha,
    resetCaptcha,
    getCaptchaResponse,
    showCaptchaError,
    isCaptchaRendered,
    isCaptchaError,
  };
};

export default useCaptcha;
